import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import BookingRescheduleDialog from '../components/BookingRescheduleDialog';
import BookingActionsMenu from '../components/BookingActionsMenu';
import RefundRulesCard from '../components/RefundRulesCard';
import StatusBadge from '../components/StatusBadge';
import { formatDateFR, formatTimeFR, formatDateLongFR } from '../utils/formatFR';
import { describeBookingStatus } from '../components/StatusBadge';
import { getBookingContextMessage, CONTEXT_TONES } from '../utils/bookingContext';
import { Calendar, Clock, Video, Star, MessageSquare, FileText, Loader2, Download, Tag, RefreshCw, XCircle, CalendarClock, Check, X, AlertTriangle, Hourglass, Ban, Shield } from 'lucide-react';
import ShareOwenContextModal from '../components/coaching/ShareOwenContextModal';

const API = getApiUrl();

const STATUS_MAP = {
  confirmed: { label: 'Confirmée', cls: 'bg-green-50 text-green-700 border border-green-200' },
  completed: { label: 'Terminée', cls: 'bg-blue-50 text-blue-700 border border-blue-200' },
  cancelled: { label: 'Annulée', cls: 'bg-red-50 text-red-600 border border-red-200' },
  expired: { label: 'Expirée', cls: 'bg-gray-100 text-gray-500 border border-gray-200' },
  pending_payment: { label: 'Paiement en attente', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
  pending: { label: 'En attente', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
};

// Heures restantes avant la séance (timezone local du client, cohérent avec Europe/Paris côté serveur)
function hoursUntil(dateStr, startTime) {
  try {
    const target = new Date(`${dateStr}T${startTime}:00`);
    return (target.getTime() - Date.now()) / 3600000;
  } catch { return 0; }
}

function RatingStars({ value, onChange, readOnly = false }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} disabled={readOnly}
          onClick={() => !readOnly && onChange(star)}
          data-testid={`rating-star-${star}`}
          className={`transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}>
          <Star className={`w-6 h-6 ${
            star <= value ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-zinc-600'
          }`} />
        </button>
      ))}
    </div>
  );
}

function Section({ title, content }) {
  if (!content) return null;
  return (
    <div>
      <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">{title}</h3>
      <p className="text-[#4B5563] text-sm leading-relaxed">{content}</p>
    </div>
  );
}

function ListSection({ title, items, bullet = 'text-[#D4AF37]' }) {
  if (!items?.length) return null;
  return (
    <div>
      <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">{title}</h3>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-[#4B5563] text-sm flex items-start gap-2">
            <span className={`${bullet} mt-1`}>•</span> {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MyBookings() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Rating dialog state
  const [ratingDialog, setRatingDialog] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  // Cancel + Reschedule states
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [requestRespondingId, setRequestRespondingId] = useState(null);

  // Report dialog state
  const [reportDialog, setReportDialog] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null);

  // Feedback history
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);
  const [linkDelay, setLinkDelay] = useState(0);

  // 🔒 Partage de contexte Owen (opt-in)
  const [shareBooking, setShareBooking] = useState(null);
  const [shareDecisions, setShareDecisions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('owen_share_decisions') || '{}'); } catch { return {}; }
  });
  const [activeShareBookingIds, setActiveShareBookingIds] = useState([]); // ids avec partage actif serveur
  const hasDecision = (id) => shareDecisions[id] || activeShareBookingIds.includes(id);
  const persistDecision = (id, v) => {
    setShareDecisions((prev) => {
      const next = { ...prev, [id]: v };
      try { localStorage.setItem('owen_share_decisions', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    fetch(`${API}/marketplace/session-link-delay`).then(r => r.json()).then(d => setLinkDelay(d.delay_minutes || 0)).catch(() => {});
  }, []);

  const isSessionLinkVisible = (b) => {
    if (!['confirmed', 'pending_payment'].includes(b.status)) return false;
    if (linkDelay === 0) return true;
    const sessionStart = new Date(`${b.date}T${b.start_time}`);
    const showFrom = new Date(sessionStart.getTime() - linkDelay * 60000);
    return new Date() >= showFrom;
  };

  const fetchBookings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      const res = await fetch(`${API}/marketplace/my-bookings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  // 🔒 Charge les partages actifs du user pour masquer le CTA sur les bookings déjà partagés
  const fetchActiveShares = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/coaching-context/my-shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const j = await r.json();
      const ids = (j.items || [])
        .map((it) => it.share?.booking_id)
        .filter(Boolean);
      setActiveShareBookingIds(ids);
    } catch (err) {
      // silencieux — le CTA reste masqué si la décision locale existe
    }
  }, [token]);

  useEffect(() => { fetchActiveShares(); }, [fetchActiveShares]);

  useEffect(() => {
    if (token) fetchBookings();
  }, [token, fetchBookings]);

  // Fetch feedback history
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/marketplace/feedback-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.history) setFeedbackHistory(d.history); })
      .catch(() => {});
  }, [token]);

  const isUpcoming = (b) => {
    const now = new Date();
    const sessionDate = new Date(`${b.date}T${b.start_time}`);
    return sessionDate > now && (b.status === 'confirmed' || b.status === 'pending_payment' || b.status === 'pending');
  };

  const canJoinSession = (b) => {
    if (!['confirmed', 'pending_payment'].includes(b.status)) return false;
    const now = new Date();
    const sessionStart = new Date(`${b.date}T${b.start_time}`);
    const sessionEnd = new Date(`${b.date}T${b.end_time}`);
    const earlyJoin = new Date(sessionStart.getTime() - 10 * 60 * 1000);
    return now >= earlyJoin && now <= sessionEnd;
  };

  const canRate = (b) => b.status === 'completed' && !b.rating;

  const handleAnalyze = async (booking) => {
    setAnalyzing(booking.booking_id);
    try {
      const res = await fetch(`${API}/marketplace/sessions/${booking.booking_id}/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success('Rapport généré et envoyé par email !');
      setReportDialog(data.report);
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAnalyzing(null);
    }
  };

  const handleViewReport = async (booking) => {
    setReportLoading(true);
    setFeedbackSubmitted(null);
    try {
      const res = await fetch(`${API}/marketplace/sessions/${booking.booking_id}/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Rapport non disponible');
      const data = await res.json();
      setReportDialog({
        ...data.report,
        _bookingId: booking.booking_id,
        _versionCount: data.version_count || 1,
        _hasPreviousVersions: data.has_previous_versions || false,
        // 🔒 Iter383/384 — Flags de verrouillage et de finalisation
        _reportLocked: !!data.report_locked,
        _reportLockedReason: data.report_locked_reason || null,
        _canRegenerate: !!data.can_regenerate,
        _canFinalize: !!data.can_finalize,
        _finalizedAt: data.finalized_at || null,
        _pdfSignature: data.pdf_signature_v1 || null,
        _regenerationsUsed: data.regenerations_used || 0,
        _regenerationsMax: data.regenerations_max || 1,
        _withinRegenWindow: data.within_regen_window !== false,
        _regenWindowHours: data.regeneration_window_hours || 24,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSubmitFeedback = async (utility) => {
    if (!reportDialog?._bookingId || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    try {
      const res = await fetch(`${API}/marketplace/sessions/${reportDialog._bookingId}/report/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ utility, report_version: reportDialog._versionCount || 1 }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Erreur'); }
      setFeedbackSubmitted(utility);
      toast.success('Merci pour votre retour !');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };


  const handleRegenerate = async (bookingId) => {
    setReportLoading(true);
    setFeedbackSubmitted(null);
    try {
      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/regenerate-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Régénération échouée');
      toast.success(`Rapport régénéré (version ${data.version_count})`);
      setReportDialog({
        ...data.report,
        _bookingId: bookingId,
        _versionCount: data.version_count || 2,
        _hasPreviousVersions: true,
      });
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleFinalize = async (bookingId) => {
    // 🔒 Iter384 — Finalisation explicite par le coach.
    // Verrou définitif + commission + workflow paiement déclenché.
    const confirmed = window.confirm(
      "Finaliser ce rapport ?\n\nUne fois finalisé :\n• Le rapport est figé (aucune régénération possible)\n• La facturation est déclenchée\n• Le client reçoit le rapport certifié\n\nConfirmer la finalisation ?"
    );
    if (!confirmed) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/report/finalize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Finalisation échouée');
      toast.success('Rapport finalisé. La facturation est déclenchée.');
      // Recharge le payload pour mettre à jour les flags
      await handleViewReport({ booking_id: bookingId });
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadPdf = async (bookingId) => {
    try {
      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/report/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur de téléchargement');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_session.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submitRating = async () => {
    if (!ratingDialog || ratingValue === 0) return;
    setSubmittingRating(true);
    try {
      const res = await fetch(`${API}/marketplace/bookings/${ratingDialog.booking_id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating: ratingValue, comment: ratingComment || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Merci pour votre évaluation !');
      setRatingDialog(null);
      setRatingValue(0);
      setRatingComment('');
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingRating(false);
    }
  };

  // ─── Booking lifecycle actions (Feb 2026) ───
  const handleReschedule = async ({ new_date, new_start_time }) => {
    const res = await fetch(`${API}/marketplace/bookings/${rescheduleBooking.booking_id}/reschedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ new_date, new_start_time }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Impossible de reporter la séance');
    }
    toast.success('Séance reprogrammée avec succès');
    fetchBookings();
  };

  const handleConfirmCancel = async () => {
    if (!cancelDialog) return;
    setCancelSubmitting(true);
    try {
      const res = await fetch(`${API}/marketplace/bookings/${cancelDialog.booking_id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      if (data.refund_status === 'refunded') {
        toast.success(`Séance annulée. Remboursement de ${data.refund_amount}€ déclenché.`);
      } else if (data.refund_status === 'not_eligible') {
        toast.success('Séance annulée (sans remboursement, <24 h)');
      } else {
        toast.success('Séance annulée');
      }
      setCancelDialog(null);
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleRespondRequest = async (booking, action) => {
    setRequestRespondingId(booking.booking_id);
    try {
      const res = await fetch(
        `${API}/marketplace/bookings/${booking.booking_id}/reschedule-request/respond`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(action === 'accept' ? 'Report accepté' : 'Report refusé');
      fetchBookings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRequestRespondingId(null);
    }
  };

  return (
    <div className="theme-light min-h-screen pt-16 sm:pt-12 pb-28 sm:pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <BackButton to="/marketplace" />
        <p className="uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-2">Coaching humain</p>
        <h1 className="text-3xl font-light text-[#111827] tracking-tight mb-6" data-testid="my-bookings-title">Mes sessions</h1>

        {/* Refund & reschedule rules (premium card — collapsible) */}
        <RefundRulesCard className="mb-6" />

        {/* Stats bar */}
        {!loading && bookings.length > 0 && (() => {
          const now = new Date();
          const upcomingCount = bookings.filter(b => new Date(`${b.date}T${b.start_time}`) > now && ['confirmed', 'pending_payment'].includes(b.status)).length;
          const completedCount = bookings.filter(b => ['completed', 'report_generated', 'to_pay', 'coach_paid'].includes(b.status)).length;
          return (
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="session-stats">
              <div className="bg-white border border-[#E8E5DE] rounded-xl p-4 text-center shadow-sm">
                <p className="text-2xl font-semibold text-[#111827]">{upcomingCount}</p>
                <p className="text-xs text-[#6B7280] mt-1">A venir</p>
              </div>
              <div className="bg-white border border-[#E8E5DE] rounded-xl p-4 text-center shadow-sm">
                <p className="text-2xl font-semibold text-[#111827]">{completedCount}</p>
                <p className="text-xs text-[#6B7280] mt-1">Terminées</p>
              </div>
              <div className="bg-white border border-[#E8E5DE] rounded-xl p-4 text-center shadow-sm">
                <p className="text-2xl font-semibold text-[#111827]">{bookings.length}</p>
                <p className="text-xs text-[#6B7280] mt-1">Total</p>
              </div>
            </div>
          );
        })()}

        {/* Upcoming sessions highlight */}
        {!loading && (() => {
          const now = new Date();
          const nextSessions = bookings
            .filter(b => new Date(`${b.date}T${b.start_time}`) > now && ['confirmed', 'pending_payment'].includes(b.status))
            .sort((a, b) => new Date(`${a.date}T${a.start_time}`) - new Date(`${b.date}T${b.start_time}`))
            .slice(0, 2);
          if (nextSessions.length === 0) return null;
          return (
            <div className="mb-8" data-testid="upcoming-sessions">
              <h2 className="text-sm font-medium text-[#111827] mb-3">Prochaines sessions</h2>
              <div className="space-y-3">
                {nextSessions.map(b => {
                  const sessionStart = new Date(`${b.date}T${b.start_time}`);
                  const diffMs = sessionStart - now;
                  const diffDays = Math.floor(diffMs / 86400000);
                  const diffHours = Math.floor((diffMs % 86400000) / 3600000);
                  const countdown = diffDays > 0 ? `Dans ${diffDays}j ${diffHours}h` : diffHours > 0 ? `Dans ${diffHours}h` : 'Imminente';
                  const joinable = canJoinSession(b);
                  const linkVisible = isSessionLinkVisible(b);

                  return (
                    <div key={b.booking_id} className="bg-gradient-to-r from-[#FEF9E7] to-white border border-[#F5E6A3] rounded-xl p-4 flex items-center justify-between gap-4"
                      data-testid={`upcoming-${b.booking_id}`}>
                      <div>
                        <p className="text-[#111827] font-medium text-sm">{b.coach_name}</p>
                        <p className="text-[#4B5563] text-xs mt-1">
                          {formatDateFR(b.date)} — {formatTimeFR(b.start_time)} – {formatTimeFR(b.end_time)}
                        </p>
                        <p className="text-[#92710A] text-xs font-medium mt-1">{countdown}</p>
                      </div>
                      {joinable && linkVisible ? (
                        <button onClick={() => navigate(`/session/${b.booking_id}`)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] rounded-full text-xs font-semibold shadow-sm whitespace-nowrap">
                          <Video className="w-3.5 h-3.5" /> Rejoindre
                        </button>
                      ) : joinable && !linkVisible ? (
                        <span className="text-[#6B7280] text-xs px-3 py-2 bg-[#F8F7F4] border border-[#E8E5DE] rounded-full">
                          Lien dans {linkDelay} min
                        </span>
                      ) : (
                        <StatusBadge booking={b} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Progression / Feedback History */}
        {feedbackHistory.length > 0 && (
          <div className="mb-8" data-testid="feedback-history">
            <h2 className="text-sm font-medium text-[#111827] mb-3">Ma progression</h2>
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-[#E8E5DE]">
                {feedbackHistory.slice(0, 5).map((fb, i) => {
                  const prevScore = feedbackHistory[i + 1]?.score_engagement || feedbackHistory[i + 1]?.score_progression;
                  const curScore = fb.score_engagement || fb.score_progression;
                  const delta = prevScore && curScore ? curScore - prevScore : null;
                  return (
                    <div key={fb.booking_id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-[#111827] text-sm font-medium">{fb.partner_name}</p>
                        <p className="text-[#6B7280] text-xs">{formatDateFR(fb.date)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {curScore && (
                          <div className="text-right">
                            <span className="text-[#111827] font-semibold text-sm">{curScore}/10</span>
                            {delta !== null && delta !== 0 && (
                              <span className={`ml-1.5 text-xs font-medium ${delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {delta > 0 ? '+' : ''}{delta}
                              </span>
                            )}
                          </div>
                        )}
                        <button onClick={() => handleViewReport({ booking_id: fb.booking_id })}
                          data-testid={`view-report-progression-${fb.booking_id}`}
                          className="text-[#92710A] text-xs font-medium hover:underline">
                          Voir
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="booking-filters">
          {[
            { value: 'all', label: 'Toutes' },
            { value: 'confirmed', label: 'Confirmées' },
            { value: 'completed', label: 'Terminées' },
            { value: 'cancelled', label: 'Annulées' },
          ].map(f => (
            <button key={f.value} onClick={() => { setFilter(f.value); setLoading(true); }}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                filter === f.value
                  ? 'bg-[#D4AF37] text-[#0A0A0B] font-medium'
                  : 'bg-white text-[#6B7280] border border-[#E8E5DE] hover:text-[#111827]'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-white border border-[#E8E5DE] flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-[#6B7280]" />
            </div>
            <h3 className="text-lg text-[#111827] mb-2">Aucune réservation</h3>
            <p className="text-[#6B7280] text-sm mb-6">Trouvez un coach et réservez votre première session.</p>
            <button onClick={() => navigate('/marketplace')}
              className="px-6 py-2.5 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
              Explorer l'Espace Coachs
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => {
              const upcoming = isUpcoming(b);
              const joinable = canJoinSession(b);
              const ratable = canRate(b);
              const hoursLeft = hoursUntil(b.date, b.start_time);

              return (
                <div key={b.booking_id} data-testid={`booking-${b.booking_id}`}
                  className={`bg-white border rounded-xl p-5 transition-all ${
                    upcoming ? 'border-[#D4AF37]/20' : 'border-[#E8E5DE]'
                  }`}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="space-y-2 min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-[#111827] font-medium">{b.coach_name}</h3>
                          <StatusBadge booking={b} />
                          {/* 🎖️ Iter384 Lot B — Badge "Présence certifiée" si les 2 parties ont été présentes avec overlap suffisant */}
                          {b.presence_proof?.both_joined && b.presence_proof?.overlap_ok && (
                            <span
                              data-testid={`presence-certified-badge-${b.booking_id}`}
                              title={`Overlap : ${b.presence_proof.overlap_seconds || 0}s (min ${b.presence_proof.min_required || 0}s)`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FFFCEC] border border-[#E8DDB5] text-[#92710A] whitespace-nowrap"
                            >
                              ✓ Présence certifiée · {Math.round((b.presence_proof.overlap_seconds || 0) / 60)} min
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> {formatDateFR(b.date)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" /> {formatTimeFR(b.start_time)} – {formatTimeFR(b.end_time)}
                          </span>
                          <span className="text-[#D4AF37] font-medium">{b.amount}€</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Join session button — primary CTA gold */}
                        {joinable && isSessionLinkVisible(b) && (
                          <button
                            data-testid={`join-session-${b.booking_id}`}
                            onClick={() => navigate(`/session/${b.booking_id}`)}
                            className="flex items-center gap-2 px-5 py-2.5 lt-btn-gold text-sm whitespace-nowrap">
                            <Video className="w-4 h-4" /> Rejoindre
                          </button>
                        )}
                        {joinable && !isSessionLinkVisible(b) && (
                          <span className="text-[#6B7280] text-xs font-body px-4 py-2.5 bg-[#F8F7F4] border border-[#E8E5DE] rounded-full">
                            Lien disponible {linkDelay} min avant
                          </span>
                        )}
                        {upcoming && !joinable && isSessionLinkVisible(b) && (
                          <button
                            data-testid={`join-session-${b.booking_id}`}
                            onClick={() => navigate(`/session/${b.booking_id}`)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] rounded-full text-sm font-medium shadow-sm whitespace-nowrap">
                            <Video className="w-4 h-4" /> Rejoindre
                          </button>
                        )}
                        {upcoming && !joinable && !isSessionLinkVisible(b) && linkDelay > 0 && (
                          <span className="text-[#6B7280] text-xs font-body px-4 py-2.5 bg-[#F8F7F4] border border-[#E8E5DE] rounded-full whitespace-nowrap">
                            Lien {linkDelay} min avant
                          </span>
                        )}

                        {/* Premium compact actions menu (…) */}
                        <BookingActionsMenu
                          testId={`actions-${b.booking_id}`}
                          actions={[
                            // Reporter — seulement si confirmée, future, pas de demande pending, et quota restant
                            b.status === 'confirmed' && upcoming
                              && b.reschedule_request?.status !== 'pending'
                              && (b.reschedule_count ?? 0) < 1 && {
                              testId: `reschedule-action-${b.booking_id}`,
                              label: 'Reporter la séance',
                              hint: 'Gratuit · 1 report maximum',
                              icon: CalendarClock,
                              onClick: () => setRescheduleBooking(b),
                            },
                            // Noter
                            ratable && {
                              testId: `rate-action-${b.booking_id}`,
                              label: 'Noter la séance',
                              icon: Star,
                              onClick: () => { setRatingDialog(b); setRatingValue(0); setRatingComment(''); },
                            },
                            // Analyser (recording dispo, pas encore de rapport)
                            b.status === 'completed' && b.has_recording && !b.ai_report && {
                              testId: `analyze-action-${b.booking_id}`,
                              label: 'Générer le rapport',
                              hint: 'Analyse IA post-séance',
                              icon: FileText,
                              disabled: analyzing === b.booking_id,
                              onClick: () => handleAnalyze(b),
                            },
                            // Voir rapport
                            ['completed', 'report_generated', 'validated', 'to_pay', 'coach_paid'].includes(b.status) && b.ai_report && {
                              testId: `view-report-action-${b.booking_id}`,
                              label: 'Voir le rapport',
                              icon: FileText,
                              onClick: () => handleViewReport(b),
                            },
                            // Télécharger PDF
                            ['completed', 'report_generated', 'validated', 'to_pay', 'coach_paid'].includes(b.status) && b.ai_report && {
                              testId: `download-pdf-action-${b.booking_id}`,
                              label: 'Télécharger le PDF',
                              icon: Download,
                              onClick: () => handleDownloadPdf(b.booking_id),
                            },
                            // Annuler — dernier, en destructive
                            b.status === 'confirmed' && upcoming
                              && b.reschedule_request?.status !== 'pending' && {
                              testId: `cancel-action-${b.booking_id}`,
                              label: 'Annuler la séance',
                              hint: hoursLeft > 24
                                ? 'Remboursement intégral (> 24 h)'
                                : 'Aucun remboursement (< 24 h)',
                              icon: XCircle,
                              destructive: true,
                              onClick: () => setCancelDialog(b),
                            },
                          ].filter(Boolean)}
                        />
                      </div>
                    </div>

                    {/* Ligne d'état contextuelle — helper unifié (cas utiles uniquement) */}
                    {(() => {
                      const msg = getBookingContextMessage(b);
                      if (!msg) return null;
                      const Icon = msg.icon;
                      return (
                        <div
                          data-testid={`context-msg-${b.booking_id}`}
                          className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 border animate-in fade-in slide-in-from-top-1 duration-200 ${CONTEXT_TONES[msg.tone] || CONTEXT_TONES.neutral}`}
                        >
                          {Icon && <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                          <span className="leading-relaxed">{msg.text}</span>
                        </div>
                      );
                    })()}

                    {/* 🔒 CTA Partage de contexte Owen — persistant tant que non-décidé */}
                    {b.status === 'confirmed' && upcoming && !hasDecision(b.booking_id) && (
                      <div
                        data-testid={`share-owen-cta-${b.booking_id}`}
                        className="flex items-center gap-3 rounded-xl px-4 py-3 border"
                        style={{ background: '#FFFCEC', borderColor: '#E8E2D2' }}
                      >
                        <Shield className="w-4 h-4 flex-shrink-0" style={{ color: '#B7901F' }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#B7901F' }}>
                            RGPD · Optionnel
                          </div>
                          <div className="text-xs mt-0.5" style={{ color: '#1F2937' }}>
                            Aidez votre coach à mieux vous accompagner en partageant votre contexte Owen (opt-in, révocable).
                          </div>
                        </div>
                        <button
                          onClick={() => setShareBooking(b)}
                          data-testid={`share-owen-btn-${b.booking_id}`}
                          className="px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap"
                          style={{
                            background: 'linear-gradient(135deg, #B7901F, #D4AF37, #F3D060)',
                            color: '#1C1147',
                          }}
                        >
                          Partager mon contexte
                        </button>
                        <button
                          onClick={() => persistDecision(b.booking_id, 'refused')}
                          data-testid={`share-owen-dismiss-${b.booking_id}`}
                          className="p-1.5 rounded-full"
                          style={{ color: '#6B7280' }}
                          title="Plus tard"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Rating display + recording indicator */}
                    {b.rating && (
                      <div className="flex items-center gap-4 pt-2 border-t border-[#E8E5DE]">
                        <div className="flex items-center gap-2">
                          <RatingStars value={b.rating} readOnly />
                          {b.rating_comment && (
                            <span className="text-[#6B7280] text-xs flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> Avis laissé
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Coach-initiated reschedule request banner */}
                    {b.status === 'confirmed' && b.reschedule_request?.status === 'pending' && (
                      <div className="border-t border-[#E8E5DE] pt-4 bg-violet-50/50 -mx-5 px-5 -mb-5 pb-5 rounded-b-xl"
                        data-testid={`reschedule-request-${b.booking_id}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <CalendarClock className="w-4 h-4 text-[#6D28D9] mt-0.5" />
                          <div className="text-sm text-[#1C1147]">
                            <p className="font-semibold">Votre coach propose un nouveau créneau</p>
                            <p className="text-[#4B3869] mt-1">
                              Nouvelle date proposée : <strong>{formatDateLongFR(b.reschedule_request.to_date)}</strong> à{' '}
                              <strong>{b.reschedule_request.to_start_time}</strong>
                            </p>
                            {b.reschedule_request.message && (
                              <blockquote className="mt-2 text-xs text-[#4B3869] italic border-l-2 border-[#6D28D9] pl-2">
                                « {b.reschedule_request.message} »
                              </blockquote>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            data-testid={`accept-reschedule-${b.booking_id}`}
                            disabled={requestRespondingId === b.booking_id}
                            onClick={() => handleRespondRequest(b, 'accept')}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-[#6D28D9] text-white rounded-full hover:bg-[#5B21B6] disabled:opacity-50">
                            <Check className="w-4 h-4" /> Accepter
                          </button>
                          <button
                            data-testid={`decline-reschedule-${b.booking_id}`}
                            disabled={requestRespondingId === b.booking_id}
                            onClick={() => handleRespondRequest(b, 'decline')}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-white text-[#1C1147] border border-gray-200 rounded-full hover:bg-gray-50 disabled:opacity-50">
                            <X className="w-4 h-4" /> Refuser
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rating Dialog */}
      <Dialog open={!!ratingDialog} onOpenChange={(open) => !open && setRatingDialog(null)}>
        <DialogContent className="bg-white border-[#DDD9D0] text-[#111827] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#111827] text-lg">
              Noter la session avec {ratingDialog?.coach_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="text-center">
              <p className="text-[#6B7280] text-sm mb-3">Comment évaluez-vous cette session ?</p>
              <div className="flex justify-center">
                <RatingStars value={ratingValue} onChange={setRatingValue} />
              </div>
              {ratingValue > 0 && (
                <p className="text-[#D4AF37] text-sm mt-2">
                  {['', 'Décevant', 'Moyen', 'Bien', 'Très bien', 'Excellent'][ratingValue]}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm text-[#4B5563] mb-2 block">Commentaire (optionnel)</label>
              <Textarea
                data-testid="rating-comment-input"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Partagez votre expérience..."
                className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] min-h-[80px]"
                maxLength={1000}
              />
            </div>

            <button
              data-testid="submit-rating-button"
              onClick={submitRating}
              disabled={ratingValue === 0 || submittingRating}
              className="w-full py-3 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              {submittingRating ? 'Envoi...' : 'Envoyer mon évaluation'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog — Premium 10 rubriques */}
      <Dialog open={!!reportDialog} onOpenChange={(open) => !open && setReportDialog(null)}>
        <DialogContent className="bg-white border-[#DDD9D0] text-[#111827] max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#111827] text-lg flex items-center justify-between gap-3 pr-6 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" /> Rapport premium
                {reportDialog?._versionCount > 1 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200">
                    v{reportDialog._versionCount}
                  </span>
                )}
                {reportDialog?._citations_meta && (
                  <span
                    title={`${reportDialog._citations_meta.grounded} citations vérifiées, ${reportDialog._citations_meta.removed} rejetées`}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                  >
                    ✓ {reportDialog._citations_meta.grounded} citations vérifiées
                  </span>
                )}
              </div>
              {reportDialog?._bookingId && reportDialog?._canFinalize && (
                <button
                  data-testid="finalize-report-btn"
                  onClick={() => handleFinalize(reportDialog._bookingId)}
                  disabled={reportLoading}
                  title="Verrouille définitivement le rapport et déclenche la facturation."
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 transition-all disabled:opacity-50"
                >
                  {reportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  Finaliser le rapport
                </button>
              )}
              {reportDialog?._bookingId && reportDialog?._canRegenerate && (
                <button
                  data-testid="regenerate-analysis-btn"
                  onClick={() => handleRegenerate(reportDialog._bookingId)}
                  disabled={reportLoading}
                  title={`${reportDialog._regenerationsUsed}/${reportDialog._regenerationsMax} régénération(s) utilisée(s) · fenêtre ${reportDialog._regenWindowHours}h`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#F8F7F4] border border-[#E8E5DE] text-[#1C1147] hover:bg-white hover:border-[#D4AF37] transition-colors disabled:opacity-50"
                >
                  {reportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Régénérer l'analyse
                </button>
              )}
              {reportDialog?._bookingId && !reportDialog?._canRegenerate && reportDialog?._reportLocked && (
                <span
                  data-testid="report-locked-badge"
                  title={reportDialog._reportLockedReason || 'Rapport verrouillé'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FAF7EC] border border-[#E8DDB5] text-[#92710A]"
                >
                  <FileText className="w-3.5 h-3.5" /> Rapport verrouillé
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {reportDialog && (
            <div className="space-y-5 pt-2">
              {/* Score badge */}
              {reportDialog.score_progression?.note != null && (
                <div className="flex items-center justify-center py-3 bg-[#D4AF37]/10 rounded-xl">
                  <span className="text-3xl font-light text-[#D4AF37]">{reportDialog.score_progression.note}/10</span>
                  {reportDialog.score_progression.justification && (
                    <span className="text-[#6B7280] text-xs ml-3 max-w-[200px]">{reportDialog.score_progression.justification}</span>
                  )}
                </div>
              )}

              <Section title="Résumé exécutif" content={reportDialog.resume} />

              {reportDialog.theme_analysis && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Analyse thématique</h3>
                  <div className="bg-[#FAF8F1] border-l-4 border-[#D4AF37] rounded-lg p-3 space-y-1.5">
                    <div className="flex flex-col sm:flex-row sm:gap-6 gap-1">
                      <p className="text-xs text-[#6B7280]"><span className="font-semibold text-[#111827]">Thème initial :</span> {reportDialog.theme_analysis.theme_initial || '—'}</p>
                      <p className="text-xs text-[#6B7280]"><span className="font-semibold text-[#111827]">Thème réellement traité :</span> {reportDialog.theme_analysis.theme_reel || '—'}</p>
                    </div>
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        reportDialog.theme_analysis.ecart_observe
                          ? 'bg-red-100 text-red-700 border border-red-200'
                          : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      }`}>
                        {reportDialog.theme_analysis.ecart_observe ? 'Écart observé' : 'Cohérent'}
                      </span>
                    </div>
                    {reportDialog.theme_analysis.explication_evolution && (
                      <p className="text-[#4B5563] text-xs leading-relaxed">{reportDialog.theme_analysis.explication_evolution}</p>
                    )}
                  </div>
                </div>
              )}

              {reportDialog.thematiques?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Thématiques abordées</h3>
                  <div className="space-y-2">
                    {reportDialog.thematiques.map((t, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-3">
                        <p className="text-[#111827] text-sm font-medium">{t.nom}</p>
                        <p className="text-[#6B7280] text-xs mt-1">{t.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ListSection title="Moments clés" items={reportDialog.moments_cles} bullet="text-[#D4AF37]" />

              {/* Signaux faibles observés — différenciateur premium */}
              {Array.isArray(reportDialog.signaux_observes) && reportDialog.signaux_observes.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Signaux faibles observés</h3>
                  <div className="space-y-2">
                    {reportDialog.signaux_observes.map((s, i) => (
                      <div key={i} className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700">
                            {s.type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {s.citation && <p className="text-[#6B7280] text-xs italic mb-1">« {s.citation} »</p>}
                        {s.lecture_pedagogique && <p className="text-[#111827] text-sm leading-relaxed">{s.lecture_pedagogique}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analyse longitudinale — si sessions passées analysées */}
              {reportDialog.analyse_longitudinale && Array.isArray(reportDialog.analyse_longitudinale.patterns_detectes) && reportDialog.analyse_longitudinale.patterns_detectes.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Analyse longitudinale</h3>
                  <div className="bg-violet-50 border-l-4 border-violet-400 rounded-r-lg p-3 space-y-2">
                    {reportDialog.analyse_longitudinale.synthese_evolution && (
                      <p className="text-[#111827] text-sm leading-relaxed mb-2">{reportDialog.analyse_longitudinale.synthese_evolution}</p>
                    )}
                    {reportDialog.analyse_longitudinale.patterns_detectes.map((p, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${
                          p.type === 'progression' ? 'bg-emerald-100 text-emerald-700' :
                          p.type === 'regression' ? 'bg-red-100 text-red-700' :
                          p.type === 'recurrence' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {p.type}
                        </span>
                        <div className="flex-1">
                          <p className="text-[#111827] text-xs">{p.description}</p>
                          {p.reference_session && (
                            <p className="text-violet-600 text-[10px] mt-0.5">→ {p.reference_session}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reportDialog.analyse_comportementale && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Analyse comportementale</h3>
                  <div className="space-y-2">
                    {reportDialog.analyse_comportementale.posture_coache && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[#6B7280] text-xs mb-1">Posture coaché</p>
                        <p className="text-[#4B5563] text-sm">{reportDialog.analyse_comportementale.posture_coache}</p>
                      </div>
                    )}
                    {reportDialog.analyse_comportementale.posture_coach && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-[#6B7280] text-xs mb-1">Posture coach</p>
                        <p className="text-[#4B5563] text-sm">{reportDialog.analyse_comportementale.posture_coach}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <ListSection title="Forces identifiées" items={reportDialog.forces} bullet="text-green-400" />
              <ListSection title="Axes d'amélioration" items={reportDialog.axes_amelioration || reportDialog.axes_travail} bullet="text-orange-400" />
              <ListSection title="Recommandations" items={reportDialog.recommandations} bullet="text-blue-400" />

              {reportDialog.plan_action?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#D4AF37] uppercase tracking-wider mb-2">Plan d'action</h3>
                  <div className="space-y-1.5">
                    {reportDialog.plan_action.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className={`text-xs px-1.5 py-0.5 rounded mt-0.5 ${
                          a.priorite === 'haute' ? 'bg-red-500/10 text-red-400' :
                          a.priorite === 'moyenne' ? 'bg-yellow-500/10 text-yellow-400' :
                          'bg-green-500/10 text-green-400'
                        }`}>{a.priorite}</span>
                        <span className="text-[#4B5563]">{a.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Section title="Prochaine étape" content={reportDialog.prochaine_etape} />

              {/* Legacy support for old format */}
              {!reportDialog.moments_cles && reportDialog.points_cles?.length > 0 && (
                <ListSection title="Points clés" items={reportDialog.points_cles} bullet="text-[#D4AF37]" />
              )}

              <p className="text-zinc-600 text-xs text-center pt-2 border-t border-[#E8E5DE]">
                Rapport premium généré par Coach DIA
              </p>

              {/* Widget feedback RLHF — aide à améliorer les rapports */}
              {reportDialog._bookingId && (
                <div className="bg-[#FAF8F1] border border-[#E8E5DE] rounded-xl p-3">
                  {feedbackSubmitted ? (
                    <p className="text-[#6B7280] text-xs text-center">
                      ✓ Retour enregistré (<span className="font-semibold">{feedbackSubmitted === 'high' ? 'Très utile' : feedbackSubmitted === 'medium' ? 'Partiellement utile' : 'Peu utile'}</span>). Merci !
                    </p>
                  ) : (
                    <>
                      <p className="text-[#111827] text-xs font-semibold mb-2 text-center">Ce rapport vous a-t-il aidé ?</p>
                      <div className="flex gap-2 justify-center">
                        <button
                          data-testid="report-feedback-high"
                          onClick={() => handleSubmitFeedback('high')}
                          disabled={feedbackSubmitting}
                          className="flex-1 py-1.5 px-3 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                        >
                          👍 Très utile
                        </button>
                        <button
                          data-testid="report-feedback-medium"
                          onClick={() => handleSubmitFeedback('medium')}
                          disabled={feedbackSubmitting}
                          className="flex-1 py-1.5 px-3 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
                        >
                          🤔 Partiel
                        </button>
                        <button
                          data-testid="report-feedback-low"
                          onClick={() => handleSubmitFeedback('low')}
                          disabled={feedbackSubmitting}
                          className="flex-1 py-1.5 px-3 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          👎 Peu utile
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {reportDialog._bookingId && (
                <button
                  data-testid="download-pdf-from-dialog"
                  onClick={() => handleDownloadPdf(reportDialog._bookingId)}
                  className="w-full mt-3 py-2.5 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Télécharger en PDF
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog (user self-service) */}
      {rescheduleBooking && (
        <BookingRescheduleDialog
          open={!!rescheduleBooking}
          onOpenChange={(o) => !o && setRescheduleBooking(null)}
          booking={rescheduleBooking}
          token={token}
          onConfirm={handleReschedule}
          title="Reporter votre séance"
          confirmLabel="Confirmer le nouveau créneau"
        />
      )}

      {/* 🔒 Modal de consentement partage de contexte Owen (CTA persistant) */}
      {shareBooking && (
        <ShareOwenContextModal
          bookingId={shareBooking.booking_id}
          coachName={shareBooking.coach_name}
          sessionDate={shareBooking.date}
          token={token}
          onClose={() => {
            persistDecision(shareBooking.booking_id, 'dismissed');
            setShareBooking(null);
          }}
          onSuccess={() => {
            persistDecision(shareBooking.booking_id, 'shared');
            fetchActiveShares();
            setShareBooking(null);
          }}
        />
      )}

      {/* Cancel confirmation dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1C1147]">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Annuler la séance ?
            </DialogTitle>
          </DialogHeader>
          {cancelDialog && (() => {
            const hrs = hoursUntil(cancelDialog.date, cancelDialog.start_time);
            const refundable = hrs > 24;
            return (
              <div className="space-y-4 py-2">
                <p className="text-sm text-[#4B3869]">
                  Séance avec <strong>{cancelDialog.coach_name}</strong><br />
                  {formatDateLongFR(cancelDialog.date)} à {cancelDialog.start_time}
                </p>
                <div className={`rounded-xl p-4 border ${refundable ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  {refundable ? (
                    <>
                      <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                        <Check className="w-4 h-4" /> Remboursement intégral
                      </p>
                      <p className="text-xs text-emerald-800/80 mt-1">
                        L'annulation intervient à plus de 24 heures de la séance.
                        Vous serez remboursé(e) de <strong>{cancelDialog.amount}€</strong> sur votre moyen de paiement d'origine (délai bancaire 5–10 jours).
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4" /> Aucun remboursement
                      </p>
                      <p className="text-xs text-amber-800/80 mt-1">
                        L'annulation intervient à moins de 24 heures de la séance. Conformément à nos conditions, aucun remboursement ne sera effectué.
                      </p>
                    </>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setCancelDialog(null)}
                    className="px-4 py-2 text-sm font-semibold text-[#4B3869] rounded-full hover:bg-gray-50">
                    Revenir
                  </button>
                  <button
                    data-testid="confirm-cancel-btn"
                    onClick={handleConfirmCancel}
                    disabled={cancelSubmitting}
                    className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
                    {cancelSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Confirmer l'annulation
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
